import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Check, AlertCircle, Loader2, Rocket } from "lucide-react";

export type MissingItem =
  | "meta_connection"
  | "ad_account"
  | "page"
  | "pixel"
  | "creative"
  | "budget"
  | "targeting"
  | "leadform"
  | "objective"
  | "copy";

interface ReadinessPanelProps {
  ready: boolean;
  missing: MissingItem[];
  isLaunching: boolean;
  onLaunch: () => void;
  onFix: (item: MissingItem) => void;
  /** Optional extra note shown above the launch button — e.g. multi-variant targeting summary */
  extraInfo?: string;
}

const MISSING_LABELS: Record<MissingItem, { labelKey: string; fixHintKey: string }> = {
  meta_connection: {
    labelKey: "campaignReadiness.items.meta_connection.label",
    fixHintKey: "campaignReadiness.items.meta_connection.fix",
  },
  ad_account: {
    labelKey: "campaignReadiness.items.ad_account.label",
    fixHintKey: "campaignReadiness.items.ad_account.fix",
  },
  page: {
    labelKey: "campaignReadiness.items.page.label",
    fixHintKey: "campaignReadiness.items.page.fix",
  },
  pixel: {
    labelKey: "campaignReadiness.items.pixel.label",
    fixHintKey: "campaignReadiness.items.pixel.fix",
  },
  creative: {
    labelKey: "campaignReadiness.items.creative.label",
    fixHintKey: "campaignReadiness.items.creative.fix",
  },
  budget: {
    labelKey: "campaignReadiness.items.budget.label",
    fixHintKey: "campaignReadiness.items.budget.fix",
  },
  targeting: {
    labelKey: "campaignReadiness.items.targeting.label",
    fixHintKey: "campaignReadiness.items.targeting.fix",
  },
  leadform: {
    labelKey: "campaignReadiness.items.leadform.label",
    fixHintKey: "campaignReadiness.items.leadform.fix",
  },
  objective: {
    labelKey: "campaignReadiness.items.objective.label",
    fixHintKey: "campaignReadiness.items.objective.fix",
  },
  copy: {
    labelKey: "campaignReadiness.items.copy.label",
    fixHintKey: "campaignReadiness.items.copy.fix",
  },
};

export function ReadinessPanel({
  ready,
  missing,
  isLaunching,
  onLaunch,
  onFix,
  extraInfo,
}: ReadinessPanelProps) {
  const { t } = useTranslation();
  return (
    <div className="bg-white/[0.04] border border-white/[0.1] rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-white">{t("campaignReadiness.title")}</h3>
        {ready ? (
          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
            {t("campaignReadiness.allReady")}
          </span>
        ) : (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
            {t("campaignReadiness.missing", { count: missing.length })}
          </span>
        )}
      </div>

      {missing.length > 0 && (
        <ul className="space-y-2">
          {missing.map((item) => {
            const meta = MISSING_LABELS[item];
            return (
              <li key={item} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex items-center gap-2 text-white/70">
                  <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  {t(meta.labelKey)}
                </span>
                <button
                  onClick={() => onFix(item)}
                  className="px-3 py-1 rounded-lg text-xs bg-white/[0.06] hover:bg-white/[0.12] border border-white/[0.1] text-white/80 hover:text-white transition-colors"
                >
                  {t(meta.fixHintKey)}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {ready && (
        <ul className="space-y-1">
          <li className="flex items-center gap-2 text-sm text-emerald-300">
            <Check className="w-4 h-4" />
            {t("campaignReadiness.allSet")}
          </li>
        </ul>
      )}

      {extraInfo && (
        <p className="text-xs text-purple-300/80 bg-purple-500/10 border border-purple-500/20 rounded-lg px-3 py-2">
          {extraInfo}
        </p>
      )}

      <motion.button
        whileHover={ready && !isLaunching ? { scale: 1.02 } : undefined}
        whileTap={ready && !isLaunching ? { scale: 0.98 } : undefined}
        disabled={!ready || isLaunching}
        onClick={onLaunch}
        className={`w-full py-3 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
          ready && !isLaunching
            ? "bg-gradient-to-r from-red-600 to-orange-500 hover:shadow-lg hover:shadow-red-600/30"
            : "bg-white/[0.06] cursor-not-allowed text-white/40"
        }`}
      >
        {isLaunching ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Rocket className="w-4 h-4" />
        )}
        {isLaunching ? t("campaignReadiness.launching") : t("campaignReadiness.launch")}
      </motion.button>
    </div>
  );
}
